"use client";

import { useReducer, useEffect, useState } from "react";
import { authReducer, initialState } from "@/lib/auth/auth-reducer";
import {
  Dialog,
  DialogContent,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
  signInWithEmail,
  verifyEmailOTP,
  sendWhatsAppOtp,
  verifyWhatsAppOtp,
  updateUserName,
  requestEmailLink,
} from "@/lib/auth/auth-actions";
import { createBrowserSupabaseClient } from "@/lib/supabase/clients/browser-client";
import WhatsappInput from "@/components/ui/whatsappInput";
import Link from "next/link";

type AuthDialogProps = {
  open: boolean;
  setOpen: (val: boolean) => void;
  initialEmail?: string;
  forceStep?: "name_input";
  context?: "chat";
};

export const AuthDialog = ({
  open,
  setOpen,
  initialEmail,
  forceStep,
  context,
}: AuthDialogProps) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [showEmailAuth, setShowEmailAuth] = useState(false);

  // Check URL param for email backdoor. Read after mount (not a render-time lazy
  // init) so SSR and first client render agree — no hydration mismatch.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowEmailAuth(params.get("auth") === "email");
  }, [open]);

  // Handle forceStep (e.g. name_input after redirect)
  useEffect(() => {
    if (open && forceStep === "name_input") {
      dispatch({ type: "SHOW_NAME_INPUT" });
    }
  }, [open, forceStep]);

  // Reset local form state when the dialog (re)opens — an open-driven reset of
  // several independent useState fields, which is the intended trigger, not a
  // render cascade to untangle.
  useEffect(() => {
    if (open && !forceStep) {
      dispatch({ type: "RESET" });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsPending(false);
      setError(null);
      setOtpInput("");
      setEmailInput("");
      if (initialEmail) {
        setEmailInput(initialEmail);
        dispatch({ type: "SET_METHOD", method: "email" });
      }
    }
  }, [open, initialEmail, forceStep]);

  const handleOpenChange = (newOpen: boolean) => {
    // Don't allow closing during OTP verification
    if (!newOpen && state.step === "verify_otp") {
      return;
    }

    if (!newOpen) {
      setOpen(false);
      dispatch({ type: "RESET" });
      setError(null);
      setOtpInput("");
      setEmailInput("");
    } else {
      setOpen(true);
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Close the dialog after auth completes.
  const finish = () => {
    setOpen(false);
    dispatch({ type: "RESET" });
  };

  // After auth completes, reveal the contact immediately — no inline email step.
  // The nurture email is captured out-of-band by the dismissible EmailBanner
  // (see hasEmailOnFile), so we don't add a screen between OTP and the contact
  // at the highest-intent moment (Red Reley §6: keep the reveal frictionless).
  const proceedAfterAuth = () => {
    finish();
  };

  // ── WhatsApp handlers ──────────────────────────────────────

  const handlePhoneSubmit = async (phoneNumber: string) => {
    setError(null);
    setIsPending(true);

    try {
      const result = await sendWhatsAppOtp(phoneNumber, state.name);

      if ("error" in result && result.error) {
        setError(result.error);
      } else {
        dispatch({ type: "SENT_OTP", phone: phoneNumber });
      }
    } finally {
      setIsPending(false);
    }
  };

  const handleWhatsAppVerify = async () => {
    setError(null);
    setIsPending(true);

    try {
      if (!state.phone) {
        setError("Número de teléfono requerido");
        return;
      }

      const result = await verifyWhatsAppOtp(state.phone, otpInput);

      if ("error" in result && result.error) {
        setError(result.error);
        return;
      }

      if (!result.tokenHash) {
        setError("Error al generar la sesión");
        return;
      }

      // Establish Supabase session using the hashed token
      const supabase = createBrowserSupabaseClient();
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: result.tokenHash,
        type: "email",
      });

      if (verifyError) {
        setError(verifyError.message);
        return;
      }

      // Session established — check if name is needed
      if (result.needsName) {
        dispatch({ type: "OTP_VERIFIED", tokenHash: result.tokenHash, needsName: true });
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.user_metadata?.display_name) {
          dispatch({ type: "SHOW_NAME_INPUT" });
        } else {
          proceedAfterAuth();
        }
      }
    } finally {
      setIsPending(false);
    }
  };

  // ── Email handlers (backdoor) ──────────────────────────────

  const handleEmailSubmit = async () => {
    if (!isValidEmail(emailInput)) {
      setError("Por favor ingresa un email válido");
      return;
    }

    setError(null);
    setIsPending(true);

    try {
      const result = await signInWithEmail(emailInput);

      if ("error" in result && result.error) {
        setError(result.error);
      } else {
        dispatch({ type: "SENT_OTP", email: emailInput });
      }
    } finally {
      setIsPending(false);
    }
  };

  const handleEmailVerify = async () => {
    setError(null);
    setIsPending(true);

    try {
      if (!state.email) {
        setError("Email requerido");
        return;
      }

      const result = await verifyEmailOTP(state.email, otpInput);

      if ("error" in result && result.error) {
        setError(result.error);
        return;
      }

      if (result.session) {
        const supabase = createBrowserSupabaseClient();
        await supabase.auth.setSession(result.session);

        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.user_metadata?.display_name) {
          dispatch({ type: "SHOW_NAME_INPUT" });
        } else {
          proceedAfterAuth();
        }
      }
    } finally {
      setIsPending(false);
    }
  };

  // ── Name handler ───────────────────────────────────────────

  const handleNameSubmit = (name: string | undefined) => {
    if (!name) {
      setError("El nombre es requerido");
      return;
    }

    // Fire-and-forget: server action updates both auth.users and public.users
    updateUserName(name).catch(() => {});

    proceedAfterAuth();
  };

  const handleEmailCaptureSubmit = async () => {
    if (!isValidEmail(emailInput)) {
      setError("Por favor ingresa un email válido");
      return;
    }
    setError(null);
    setIsPending(true);
    try {
      // Verified path: send a confirmation link (email becomes real only after
      // the user clicks it). More friction than a silent nurture save, but the
      // email is worth more verified.
      const res = await requestEmailLink(emailInput);
      if (res && "error" in res && res.error) {
        setError(res.error);
        return;
      }
      // Close the dialog and hand the "Revisa tu correo" confirmation to the
      // top EmailBanner (it owns that sent-state UI).
      window.dispatchEvent(
        new CustomEvent("jaro:email-link-sent", { detail: { email: emailInput } })
      );
      finish();
    } catch {
      setError("No pudimos enviar el enlace. Intenta de nuevo.");
    } finally {
      setIsPending(false);
    }
  };

  // ── Render: Phone input (default view) ─────────────────────

  const renderPhoneInput = () => (
    <div className="flex flex-col gap-6">
      <div className="text-center space-y-2">
        {context === "chat" ? (
          <>
            <h2 className="text-2xl font-bold text-slate-800">
              Entra <span className="text-green-600">Gratis</span> para contactarlos
            </h2>
            <p className="text-slate-600">
              Verifica tu número para conectarte con el transportista.
            </p>
            <p className="font-semibold text-sm">
              Es rápido — sin contraseñas.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-slate-800">
              Accede <span className="text-green-600">Gratis</span> al Directorio
            </h2>
            <p className="text-slate-600">
              Recibe información de transportistas directo en tu WhatsApp.
            </p>
            <p className="font-semibold text-sm">
              Sin contraseñas, sin spam, sin tarjeta de crédito.
            </p>
          </>
        )}
      </div>

      <div className="space-y-4">
        <WhatsappInput onSubmit={handlePhoneSubmit} isLoading={isPending} />
      </div>

      {showEmailAuth && (
        <>
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-4 text-slate-500 font-medium">
                O continuar con email
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium text-slate-700"
              >
                Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="Ingresa tu email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleEmailSubmit();
                }}
                className="h-11 border-2 border-slate-200 focus:border-primary focus-visible:ring-0 transition-colors"
              />
            </div>
            <Button
              onClick={handleEmailSubmit}
              className="w-full h-11 bg-primary hover:bg-primary/90 text-white transition-colors"
              disabled={isPending || !emailInput || !isValidEmail(emailInput)}
            >
              {isPending ? "Enviando..." : "Continuar con Email"}
            </Button>
          </div>
        </>
      )}

      <p className="text-sm text-slate-500 text-center">
        Al continuar aceptas los{" "}
        <Link href="/terminos-y-condiciones" target="_blank" className="text-primary hover:underline">
          términos y condiciones
        </Link>{" "}
        y la{" "}
        <Link href="/politica-de-privacidad" target="_blank" className="text-primary hover:underline">
          política de privacidad
        </Link>
      </p>
    </div>
  );

  // ── Render: Email input (backdoor only) ────────────────────

  const renderEmailInput = () => (
    <div className="flex flex-col gap-6">
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-bold text-slate-800">
          Iniciar Sesión
        </h2>
        <p className="text-slate-600">Ingresa tu email</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-slate-700">
            Email
          </label>
          <Input
            id="email"
            type="email"
            placeholder="Ingresa tu email"
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleEmailSubmit();
            }}
            className="h-11 border-2 border-slate-200 focus:border-primary focus-visible:ring-0 transition-colors"
          />
        </div>
        <Button
          onClick={handleEmailSubmit}
          className="w-full h-11 bg-primary hover:bg-primary/90 text-white transition-colors"
          disabled={isPending || !emailInput || !isValidEmail(emailInput)}
        >
          {isPending ? "Enviando..." : "Continuar con Email"}
        </Button>
      </div>

      <Button variant="ghost" onClick={() => dispatch({ type: "BACK" })}>
        Regresar
      </Button>
    </div>
  );

  // ── Render: OTP verification ───────────────────────────────

  const renderOtpVerification = () => {
    const isWhatsApp = state.method === "whatsapp";
    const handleVerify = isWhatsApp ? handleWhatsAppVerify : handleEmailVerify;
    const sentTo = isWhatsApp ? state.phone : state.email;

    return (
      <div className="flex flex-col gap-4 items-center">
        <label className="text-lg font-semibold text-slate-800">
          Ingresa el código de verificación
        </label>
        <InputOTP
          maxLength={6}
          value={otpInput}
          onChange={(value) => {
            setOtpInput(value);
            setError(null);
          }}
          onComplete={handleVerify}
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
        <p className="text-sm text-muted-foreground">
          Código enviado a {sentTo}
        </p>
        <Button
          onClick={handleVerify}
          disabled={isPending || otpInput.length < 6}
          className="w-full"
        >
          {isPending ? "Verificando..." : "Verificar Código"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            dispatch({ type: "BACK" });
            setError(null);
            setOtpInput("");
          }}
        >
          Regresar
        </Button>
      </div>
    );
  };

  // ── Render: Name input ─────────────────────────────────────

  const renderNameInput = () => (
    <div className="flex flex-col gap-4">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-slate-800">Un último paso</h2>
        <p className="text-slate-600">
          Por favor, ingresa tu nombre para completar el registro
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium text-slate-700">
            Nombre
          </label>
          <Input
            id="name"
            type="text"
            placeholder="Ingresa tu nombre"
            onChange={(e) => dispatch({ type: "SUBMIT_NAME", name: e.target.value })}
            className="h-11 border-2 border-slate-200 focus:border-primary focus-visible:ring-0 transition-colors"
          />
        </div>
        <Button
          onClick={() => handleNameSubmit(state.name)}
          className="w-full h-11 bg-primary hover:bg-primary/90 text-white transition-colors"
          disabled={!state.name}
        >
          Completar registro
        </Button>
      </div>
      <p className="text-sm text-slate-500 text-center">
        Por favor completa tu registro antes de cerrar
      </p>
    </div>
  );

  // ── Render: Optional email capture (after phone verification) ──

  const renderEmailCapture = () => (
    <div className="flex flex-col gap-5">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-slate-800">
          ¿Te enviamos opciones por correo?
        </h2>
        <p className="text-slate-600">
          Opcional — déjanos tu email y te compartimos transportistas que te sirvan.
        </p>
      </div>

      <div className="space-y-4">
        <Input
          id="capture-email"
          type="email"
          placeholder="tu@email.com"
          value={emailInput}
          onChange={(e) => {
            setEmailInput(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleEmailCaptureSubmit();
          }}
          className="h-11 border-2 border-slate-200 focus:border-primary focus-visible:ring-0 transition-colors"
        />
        <Button
          onClick={handleEmailCaptureSubmit}
          className="w-full h-11 bg-primary hover:bg-primary/90 text-white transition-colors"
          disabled={isPending || !emailInput || !isValidEmail(emailInput)}
        >
          {isPending ? "Enviando..." : "Enviar enlace de confirmación"}
        </Button>
        <button
          onClick={finish}
          className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          Ahora no
        </button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[425px] p-6 shadow-xl">
          <DialogTitle className="text-2xl font-bold text-slate-800"></DialogTitle>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md mb-4">
              {error}
            </div>
          )}

          {state.step === "phone_input" && renderPhoneInput()}
          {state.step === "email_input" && renderEmailInput()}
          {state.step === "verify_otp" && renderOtpVerification()}
          {state.step === "name_input" && renderNameInput()}
          {state.step === "email_capture" && renderEmailCapture()}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
